// PropOracle LLC — Legal Documents
// Last updated: April 21, 2026
// Source of truth: frontend/constants/legalDocuments.js

export const PRIVACY_POLICY_TEXT = `PRIVACY POLICY & DATA PRACTICES
Last updated: April 21, 2026

PLEASE READ THIS PRIVACY POLICY CAREFULLY BEFORE USING PROPORACLE.

By creating an account or using any feature of PropOracle, you acknowledge that you have read and understood this Privacy Policy.

1. INFORMATION WE COLLECT

When you create an account and use PropOracle, we collect the following information:

• Account Information: Your email address and display name provided at registration.
• Chat & Activity Logs: Conversations you have with the PropOracle AI, including queries, AI responses, and any bet prompts generated or saved within the app.
• Usage Data: General app usage patterns such as session frequency and features accessed.
• Device Information: Basic device identifiers required for app functionality and session management.

We do NOT collect payment information, location data, or any information beyond what is necessary to operate the app.

2. HOW WE USE YOUR DATA

All data collected is used exclusively for in-app operations, including:

• Authenticating your identity and maintaining your session
• Persisting your chat history so you can review past conversations
• Tracking saved bets and performance in your personal stats
• Improving AI response quality and app reliability
• Responding to support requests you initiate

We do NOT use your data for advertising, profiling, or any purpose outside of delivering the PropOracle service to you.

3. DATA SHARING & THIRD PARTIES

We do NOT sell, rent, trade, or otherwise transfer your personal data to any third party for commercial purposes.

We may share data only in the following limited circumstances:

• Service Providers: Third-party vendors that provide essential infrastructure and operational services on our behalf, under strict confidentiality obligations, and solely as directed by PropOracle.
• Legal Compliance: If required by law, court order, or governmental authority.
• Safety: To protect the rights, property, or safety of PropOracle, its users, or the public.

Any third-party services we engage are governed by their own privacy policies and are used solely to support the operation of PropOracle.

4. DATA RETENTION

Your account data, chat logs, and saved bets are retained for as long as your account is active. If you delete your account, your data will be permanently deleted from our systems within 30 days.

Session data and device tokens are deleted immediately upon sign-out.

5. DATA SECURITY

We implement reasonable administrative, technical, and physical safeguards to protect your personal information from unauthorized access, loss, misuse, or alteration. This includes industry-standard encryption for data in transit and at rest, as well as secure credential handling on your device.

No method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.

6. YOUR RIGHTS

Depending on your jurisdiction, you may have the right to:

• Access the personal data we hold about you
• Request correction of inaccurate data
• Request deletion of your account and associated data
• Withdraw consent at any time by deleting your account

To exercise any of these rights, contact us at admin@proporacleai.com.

7. CHILDREN'S PRIVACY

PropOracle is intended for users aged 21 and older (or the legal gambling age in your jurisdiction). We do not knowingly collect personal information from minors. If we become aware that a user under the applicable legal age has provided personal information, we will take steps to delete that information promptly.

8. CHANGES TO THIS POLICY

We may update this Privacy Policy from time to time. We will notify you of material changes via in-app notification or email. Continued use of PropOracle after changes are posted constitutes your acceptance of the revised policy.

9. RESPONSIBLE GAMBLING

PropOracle is a research and informational tool. We are not a sportsbook and do not facilitate wagers. If you or someone you know has a gambling problem, call 1-800-GAMBLER (1-800-426-2537) for free, confidential help.

10. CONTACT

For privacy-related questions or data requests, contact us at:
admin@proporacleai.com
PropOracle LLC
New York, NY
Governing Law: State of New York`;


export const TERMS_TEXT = `TERMS OF SERVICE
Last updated: April 21, 2026

PLEASE READ THESE TERMS CAREFULLY BEFORE USING PROPORACLE.

By accessing or using PropOracle, you agree to be bound by these Terms of Service. If you do not agree, you may not use the app.

1. ACCEPTANCE OF TERMS

By creating an account or using any feature of PropOracle, you confirm that you are at least 21 years of age (or the legal gambling age in your jurisdiction), that you have read and understood these Terms, and that you agree to comply with them. By creating an account, you affirmatively represent and warrant that you meet the minimum age requirement applicable in your jurisdiction.

2. DESCRIPTION OF SERVICE

PropOracle is an AI-powered sports analytics application that provides informational content, statistical analysis, and prop bet research tools. PropOracle is strictly an informational and entertainment platform. It does not place, accept, or facilitate wagers of any kind.

3. NOT BETTING ADVICE

All content generated by PropOracle — including AI analysis, probability estimates, hit rates, player projections, and parlay suggestions — is provided for informational and entertainment purposes only.

Nothing in this application constitutes:
• Professional sports betting advice
• Financial advice or investment recommendations
• A guarantee of any outcome or profit

Past performance and historical hit rates do not guarantee future results. Sports outcomes are inherently unpredictable.

4. USER RESPONSIBILITIES

You agree that you are solely responsible for:
• Any wager you place based on information from PropOracle
• Verifying that sports betting is legal in your jurisdiction before using this app
• Your own financial decisions and any resulting losses
• Maintaining the confidentiality of your account credentials

5. LIMITATION OF LIABILITY

TO THE FULLEST EXTENT PERMITTED BY LAW, PROPORACLE, ITS DEVELOPERS, OPERATORS, OFFICERS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY:

• Betting or gambling losses of any amount
• Financial losses or damages of any kind
• Loss of data or profits
• Indirect, incidental, consequential, or punitive damages

ARISING FROM YOUR USE OF, OR INABILITY TO USE, PROPORACLE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.

YOUR USE OF PROPORACLE IS AT YOUR SOLE RISK.

6. INDEMNIFICATION

You agree to indemnify, defend, and hold harmless PropOracle and its operators from any claims, losses, liabilities, damages, costs, or expenses (including reasonable legal fees) arising out of your use of the app, your violation of these Terms, or your violation of any applicable law.

7. INTELLECTUAL PROPERTY

All content, design, code, and AI-generated outputs within PropOracle are the property of PropOracle LLC or its licensors. PropOracle grants you a limited, non-exclusive, non-transferable, revocable license to use the app for your personal, non-commercial purposes only. AI-generated outputs, analytics, and parlay suggestions may not be scraped, redistributed, resold, or used to build competing products. You may not reproduce, distribute, or create derivative works from any part of the app without express written consent from PropOracle LLC.

8. ACCOUNT TERMINATION

We reserve the right to suspend or terminate your account at any time, with or without notice, if we believe you have violated these Terms or engaged in any activity that is harmful to other users or the platform. Upon termination, your right to use PropOracle ceases immediately. Sections 5, 6, 7, and 12 of these Terms survive termination.

9. MODIFICATIONS TO TERMS

We reserve the right to modify these Terms at any time. Material changes will be communicated via in-app notification or email. Continued use of PropOracle after changes take effect constitutes acceptance of the revised Terms.

10. RESPONSIBLE GAMBLING

PropOracle is a research and informational tool. We are not a sportsbook and do not facilitate wagers. If you or someone you know has a gambling problem, call 1-800-GAMBLER (1-800-426-2537) for free, confidential help.

11. CONTACT

For questions about these Terms, contact us at:
admin@proporacleai.com
PropOracle LLC
New York, NY

12. GOVERNING LAW & DISPUTE RESOLUTION

These Terms are governed by the laws of the State of New York, without regard to conflict of law principles. Any dispute arising out of or relating to these Terms or your use of PropOracle shall be resolved by binding individual arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules, conducted in New York, NY. You waive any right to participate in a class action lawsuit or class-wide arbitration. Nothing in this section prevents either party from seeking relief in small claims court for disputes within that court's jurisdiction.

13. BILLING & SUBSCRIPTIONS

PropOracle offers the following auto-renewable subscription plans through the Apple App Store:

• PropOracle Pro Weekly — $6.99 per week
• PropOracle Pro Monthly — $29.99 per month

Payment will be charged to your Apple ID account at confirmation of purchase. Your subscription automatically renews unless auto-renewal is turned off at least 24 hours before the end of the current billing period. Your account will be charged for renewal within 24 hours prior to the end of the current period at the rate of your selected plan.

You may manage or cancel your subscription at any time through your Apple ID account settings: Settings > [your name] > Subscriptions. Cancellation takes effect at the end of the current paid period — you will retain access until that date.

All purchases, subscription billing, renewals, and refunds are processed exclusively by Apple Inc. through the App Store. PropOracle does not collect or store payment information. For refund requests, contact Apple Support directly. PropOracle has no ability to issue refunds for App Store purchases.

14. GEOGRAPHIC RESTRICTIONS & LEGAL COMPLIANCE

PropOracle is intended for use only in jurisdictions where accessing sports analytics and betting-related informational content is lawful. PropOracle makes no representation that the app or its content is appropriate, legal, or available for use in any specific location.

You are solely and exclusively responsible for:
• Knowing and complying with all federal, state, local, and international laws applicable to your use of this app, including laws governing sports betting, online gambling, and gambling-related informational services in your jurisdiction
• Determining whether accessing or using PropOracle is legal where you are located at the time of use
• Monitoring changes to applicable laws in your jurisdiction on an ongoing basis — compliance is a continuing obligation, not a one-time check at registration

PropOracle expressly disclaims any responsibility or liability for your access to or use of the app in any jurisdiction where such use is restricted, prohibited, or regulated. If you access PropOracle from a jurisdiction where doing so is unlawful, you do so entirely at your own risk and in material violation of these Terms.

You agree to indemnify and hold harmless PropOracle LLC and its operators from any claims, fines, penalties, or liabilities arising from your use of the app in violation of the laws of your jurisdiction.

15. GENERAL PROVISIONS

If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect. These Terms constitute the entire agreement between you and PropOracle LLC and supersede any prior agreements or understandings, whether written or oral, relating to your use of the app.`;
